import React from "react";
import { useNavigate } from "react-router-dom";
import { BRANDING, PLANS } from "@/config/branding";
import { GoldButton, OutlineButton } from "@/components/Primitives";
import { DisclaimerFooter } from "@/components/Disclaimer";
import { Check, ArrowRight, ShieldCheck, Inbox, Compass, FileText, Sparkles } from "lucide-react";

const SYM = { 11: "$", 19: "$", 39: "$" };

const HIGHLIGHTS = [
  { icon: Inbox, title: "Financial Inbox", body: "Receipts, bills and statements — sorted, named and organised for you." },
  { icon: Compass, title: "AI Advisory Team", body: "A calm panel of virtual advisers reviews your numbers and guides you." },
  { icon: FileText, title: "Tax-ready Exports", body: "Polished PDF, Word and Excel reports for your accountant or broker." },
  { icon: ShieldCheck, title: "Private & Secure", body: "One calm, secure home for every part of your financial life." },
];

export default function Landing() {
  const navigate = useNavigate();

  const choose = (planKey) => {
    if (planKey) localStorage.setItem("finetomi_plan", planKey);
    navigate("/auth?mode=register");
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1] text-[#2C2A28]">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 md:px-12 py-6 max-w-7xl mx-auto">
        <div>
          <div className="font-display text-3xl tracking-[0.08em] gold-text">{BRANDING.name}</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A99F90]">{BRANDING.tagline}</div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="landing-signin" onClick={() => navigate("/auth?mode=login")}
            className="text-sm text-[#6b6256] hover:text-[#2C2A28] transition-colors px-3 py-2">Sign in</button>
          <GoldButton data-testid="landing-get-started" onClick={() => choose("")}>Get started</GoldButton>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[460px] h-[460px] rounded-full bg-[#C6A45C]/12 blur-3xl" />
        <div className="absolute -bottom-40 -left-24 w-[420px] h-[420px] rounded-full bg-[#9FB8C9]/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8a6d2f] bg-[#C6A45C]/10 border border-[#C6A45C]/25 rounded-full px-4 py-1.5 mb-6">
              <Sparkles size={13} /> The Financial Operating System
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              Finally, one place for your entire financial life.
            </h1>
            <p className="text-[#6b6256] text-base mt-6 max-w-md leading-relaxed">
              Finetomi brings your income, spending, savings, documents and investments together — then your AI
              advisory team turns it all into calm, practical guidance.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <GoldButton data-testid="hero-get-started" onClick={() => choose("")} className="h-12 px-7">
                Start your Financial Home <ArrowRight size={16} />
              </GoldButton>
              <OutlineButton data-testid="hero-signin" onClick={() => navigate("/auth?mode=login")} className="h-12 px-6">I already have an account</OutlineButton>
            </div>
            <button data-testid="hero-see-plans" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
              className="text-sm text-[#8A7F70] hover:text-[#8a6d2f] mt-5 transition-colors">Explore plans &amp; pricing ↓</button>
          </div>
          <div className="relative fade-up hidden lg:block">
            <div className="absolute -inset-6 rounded-full bg-[#C6A45C]/10 blur-3xl" />
            <img
              src="https://customer-assets.emergentagent.com/job_finplan-hub-36/artifacts/w4g0mbwc_ball%20rings.PNG"
              alt="Finetomi — floating gold spheres" className="relative rounded-3xl shadow-[0_30px_80px_rgba(44,42,40,0.18)] w-full h-[460px] object-cover" />
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HIGHLIGHTS.map((h, i) => (
            <div key={i} className="glass-card rounded-2xl p-6 fade-up" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="w-11 h-11 rounded-full bg-[#F0E7D8] flex items-center justify-center mb-4">
                <h.icon size={20} className="text-[#C6A45C]" strokeWidth={1.5} />
              </div>
              <div className="font-display text-lg">{h.title}</div>
              <p className="text-sm text-[#8A7F70] mt-2 leading-relaxed">{h.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-light">Choose the plan that fits your life</h2>
          <p className="text-[#8A7F70] text-sm mt-3">Start in minutes. Change or cancel anytime.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`plan-card-${p.key}`}
              className={`rounded-3xl p-8 border transition-all relative ${
                p.featured ? "border-[#C6A45C] bg-white shadow-[0_20px_60px_rgba(198,164,92,0.22)] md:-translate-y-3"
                           : "border-[#EAE0D2] bg-white/70 hover:border-[#D8BE83]"}`}>
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] bg-gradient-to-br from-[#D8BE83] to-[#C6A45C] text-[#3a2f1a] px-3 py-1 rounded-full font-medium">
                  Most popular
                </div>
              )}
              <div className="font-display text-2xl">{p.name}</div>
              <p className="text-sm text-[#8A7F70] mt-1 min-h-[40px]">{p.tagline}</p>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display text-5xl font-light gold-text">{SYM[p.price]}{p.price}</span>
                <span className="text-[#A99F90] text-sm">/ month</span>
              </div>
              <ul className="mt-6 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#6b6256]">
                    <Check size={16} className="text-[#8FB996] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <GoldButton data-testid={`plan-choose-${p.key}`} onClick={() => choose(p.key)} className="w-full mt-8 h-12">
                Choose {p.name} <ArrowRight size={16} />
              </GoldButton>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#EAE0D2] mt-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="font-display text-xl gold-text">{BRANDING.name}</div>
          <DisclaimerFooter className="text-center sm:text-right" />
        </div>
      </footer>
    </div>
  );
}
